import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { type Profile } from '@/types/profile';
import { authenticatedGet } from '../utils/apiUtils';
import { useAuth } from './AuthContext';
import { authStateChangeSignal } from './AuthContext';

// Function to get complete S3 URL if needed
export const getFullProfileImageUrl = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  
  // If the URL is already complete (contains http), return it
  if (url.startsWith('http')) return url;
  
  // If the URL is just a path (shortened), reconstruct it
  if (url.startsWith('/')) {
    // Base S3 bucket URL - this should match your backend configuration
    const baseS3Url = 'https://wanttd-service-s3.s3.amazonaws.com';
    return `${baseS3Url}${url}`;
  }
  
  return url;
};

interface ProfileContextType {
  profile: Profile | null;
  loading: boolean;
  error: string | null;
  fetchProfile: () => Promise<Profile | null>;
  clearProfile: () => void;
  updateProfileContext: (updatedProfile: Profile) => void;
}

const initialProfile: Profile = {
  username: "",
  bio: "",
  contactInfo: "",
  skills: [],
  industries: [],
  socialLinks: []
};

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

// Cache control - profile data will be considered stale after this time
const PROFILE_CACHE_TIME = 5 * 60 * 1000; // 5 minutes in milliseconds
let lastFetchTime = 0;

export const ProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Function to fetch profile data
  const fetchProfile = useCallback(async (): Promise<Profile | null> => {
    // If we have a cached profile and it's not stale, return it
    const now = Date.now();
    if (profile && now - lastFetchTime < PROFILE_CACHE_TIME) {
      console.log('Using cached profile data');
      return profile;
    }

    // If not authenticated, don't try to fetch
    if (!isAuthenticated || !user) {
      setProfile(null);
      return null;
    }

    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching profile data from API');
      const response = await authenticatedGet(`${import.meta.env.VITE_API_URL}/profiles/me`);
      
      if (response.ok) {
        const profileData = await response.json();
        console.log('Profile data received:', profileData);
        
        // Handle both property names for profile image URL
        // Check if profileImageUrl exists in the response and map it to profilePictureUrl
        if (profileData.profileImageUrl) {
          // For frontend compatibility, we'll keep both fields in sync
          profileData.profilePictureUrl = profileData.profileImageUrl;
          
          // Ensure the profile picture URL is expanded if it's a shortened one
          profileData.profileImageUrl = getFullProfileImageUrl(profileData.profileImageUrl);
          profileData.profilePictureUrl = profileData.profileImageUrl;
        } 
        // If only profilePictureUrl exists (unlikely, but for completeness)
        else if (profileData.profilePictureUrl) {
          // Keep both fields in sync
          profileData.profileImageUrl = profileData.profilePictureUrl;
          
          // Ensure URLs are expanded
          profileData.profilePictureUrl = getFullProfileImageUrl(profileData.profilePictureUrl);
          profileData.profileImageUrl = profileData.profilePictureUrl;
        } 
        // If Google profile picture URL is available from auth context but no profile image yet
        else if (user?.profilePictureUrl) {
          profileData.profilePictureUrl = user.profilePictureUrl;
          profileData.profileImageUrl = user.profilePictureUrl;
        }

        // Ensure privacy settings are properly initialized
        if (!profileData.privacySettings) {
          // If no privacy settings exist, create default ones
          profileData.privacySettings = {
            id: "",
            userId: profileData.userId || "",
            profileVisibility: "PUBLIC",
            showReviews: true,
            showWillHistory: true,
            // Use the direct showContactInfo property if available, otherwise default to true
            showContactInfo: profileData.showContactInfo ?? true
          };
          console.log('Created default privacy settings:', profileData.privacySettings);
        } else {
          // Make sure showContactInfo property is synced with privacySettings
          profileData.showContactInfo = profileData.privacySettings.showContactInfo;
          console.log('Using existing privacy settings:', profileData.privacySettings);
        }
        
        setProfile(profileData);
        lastFetchTime = Date.now(); // Update the fetch time
        return profileData;
      } else {
        // If the profile doesn't exist yet, we return null but don't set an error
        if (response.status === 404) {
          console.log('Profile not found - user may need to create one');
          
          // Create a minimal profile with just username and optional Google profile picture
          if (user) {
            const minimalProfile: Profile = {
              username: user.username || user.email.split('@')[0],
              bio: null,
              contactInfo: user.email,
              skills: [],
              industries: [],
              socialLinks: [],
              // Use Google profile picture if available
              profilePictureUrl: user.profilePictureUrl,
              profileImageUrl: user.profilePictureUrl
            };
            setProfile(minimalProfile);
            return minimalProfile;
          }
          
          setProfile(null);
          return null;
        }
        
        throw new Error('Failed to load profile');
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Failed to load profile data');
      return null;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  // After the fetchProfile function definition and before the clearProfile function
  // Add a function to directly update the profile in context
  const updateProfileContext = useCallback((updatedProfile: Profile): void => {
    // Process the profile image URLs if they exist
    if (updatedProfile.profileImageUrl) {
      // For frontend compatibility, keep both fields in sync
      updatedProfile.profilePictureUrl = updatedProfile.profileImageUrl;
      
      // Ensure the profile picture URL is expanded if it's a shortened one
      updatedProfile.profileImageUrl = getFullProfileImageUrl(updatedProfile.profileImageUrl);
      updatedProfile.profilePictureUrl = updatedProfile.profileImageUrl;
    } 
    else if (updatedProfile.profilePictureUrl) {
      // Keep both fields in sync
      updatedProfile.profileImageUrl = updatedProfile.profilePictureUrl;
      
      // Ensure URLs are expanded
      updatedProfile.profilePictureUrl = getFullProfileImageUrl(updatedProfile.profilePictureUrl);
      updatedProfile.profileImageUrl = updatedProfile.profilePictureUrl;
    }

    // Update the profile state directly
    setProfile(updatedProfile);
    
    // Reset the fetch time so we know it's fresh
    lastFetchTime = Date.now();
    
    console.log('Profile context updated directly with:', updatedProfile);
  }, []);

  // Clear profile data (useful on logout)
  const clearProfile = useCallback(() => {
    setProfile(null);
    lastFetchTime = 0;
  }, []);

  // Listen for auth state changes
  useEffect(() => {
    const handleAuthStateChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      
      if (customEvent.detail && !customEvent.detail.isAuthenticated) {
        // User logged out, clear profile
        clearProfile();
      }
    };
    
    authStateChangeSignal.addEventListener('authStateChange', handleAuthStateChange);
    
    return () => {
      authStateChangeSignal.removeEventListener('authStateChange', handleAuthStateChange);
    };
  }, [clearProfile]);

  // Clear profile when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      clearProfile();
    }
  }, [isAuthenticated, clearProfile]);

  return (
    <ProfileContext.Provider
      value={{
        profile,
        loading,
        error,
        fetchProfile,
        clearProfile,
        updateProfileContext,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
}; 